'use client';

import Link from 'next/link';
import React, { useCallback, useEffect, useState } from 'react';
import NextImage from 'src/components/_common/NextImage';
import { useMainBannerListQuery } from 'src/store/server/main/main.query';
import { momentTimezone } from 'src/utils/time';
import utils from 'src/utils/utils';
import type Swiper from 'swiper';
import { Autoplay, Pagination } from 'swiper';
import { SwiperSlide } from 'swiper/react';

import BannerStyled from './Banner.styled';

const MINIMUM_SLIDES_FOR_AUTOPLAY = 1;

const Banner = () => {
  const [swiperInstance, setSwiperInstance] = useState<Swiper | null>(null);

  const { data: mainBannerList, isLoading: isMainBannerListLoading } = useMainBannerListQuery();

  const formatMainEventDate = useCallback((startDateTime: string | null, endDateTime: string | null) => {
    if (!startDateTime || !endDateTime) {
      return '';
    }

    const startDate = momentTimezone(startDateTime).format('YYYY년 M월 D일');
    const endDate = momentTimezone(endDateTime).format('YYYY년 M월 D일');
    return `${startDate}부터 ${endDate}까지`;
  }, []);

  useEffect(() => {
    // 메인 이벤트가 1개면 swiper 자동 재생 중지
    if (swiperInstance && swiperInstance.autoplay) {
      if (mainBannerList && mainBannerList.count > MINIMUM_SLIDES_FOR_AUTOPLAY) {
        swiperInstance.autoplay.start();
      }

      if (!mainBannerList || mainBannerList.count <= MINIMUM_SLIDES_FOR_AUTOPLAY) {
        swiperInstance.autoplay.stop();
      }
    }
  }, [swiperInstance, mainBannerList]);

  return (
    <BannerStyled.Container>
      {/* 데이터가 없거나 loading중일 때는 skeleton 표시 */}
      {(!mainBannerList || isMainBannerListLoading) && <BannerStyled.Skeleton />}
      {/* 데이터가 있고, loading 중이지 않다면 콘텐츠 표시 */}
      {mainBannerList && !isMainBannerListLoading && (
        <BannerStyled.CustomSwiper
          onSwiper={setSwiperInstance}
          modules={[Pagination, Autoplay]}
          loop
          pagination={{
            clickable: true,
            type: 'fraction',
            el: '.custom-pagination-fraction',
            renderFraction: (currentClass, totalClass) =>
              `<span class="${currentClass}"></span>` +
              '<span class="swiper-pagination-separator">/</span>' +
              `<span class="${totalClass}"></span>`,
          }}
          autoplay={{
            delay: 5000,
            disableOnInteraction: false,
          }}
        >
          {mainBannerList.rows.map((mainBanner) => (
            <SwiperSlide key={mainBanner.bannerSequence}>
              <Link
                href={mainBanner.linkURL ?? '/'}
                passHref
                {...utils.getOpenNewWindowProps(mainBanner.blank === 'Y')}
                legacyBehavior>
                <BannerStyled.ImageWrapper>
                  <NextImage
                    src={mainBanner.mobileImagePath}
                    /**
                     * 이미지 내부에 텍스트가 포함되어 있어
                     * 1. 스크린리더 사용자를 위한 접근성
                     * 2. 검색엔진 최적화(SEO)
                     * 를 위해 제목, 부제목, 기간 등 모든 정보를 대체 텍스트로 제공
                     */
                    alt={`[배너] ${mainBanner.bannerName} ${formatMainEventDate(
                      mainBanner.bannerStartDateTime,
                      mainBanner.bannerEndDateTime,
                    )}`}
                    width={360}
                    height={360}
                    layout="responsive"
                    objectFit="cover"
                    loading="eager"
                    priority
                  />
                </BannerStyled.ImageWrapper>
              </Link>
            </SwiperSlide>
          ))}
          <BannerStyled.CustomPagination
            className="custom-pagination-fraction"
            role="status"
            aria-label="배너 페이지"
          />
        </BannerStyled.CustomSwiper>
      )}
    </BannerStyled.Container>
  );
};

export default Banner;
